import React from 'react';
import { EMZI_COLORS, EMZI_RADIUS, EMZI_SPACING } from '../../styles/emziCI';
import StructuredListInput from './StructuredListInput';
import { BILINGUAL_REQUIRED } from '../../utils/formValidation';
import BilingualFieldWithHelper from './BilingualFieldWithHelper';
import ControlledTextarea from '../ControlledTextarea';

/**
 * EnhancedAmbassadorForm - Ambassador pack form with Input Quality Layer enhancements
 * Focus: Brand representatives, influencers, spokesperson info (BILINGUAL)
 */
const EnhancedAmbassadorForm = ({ 
  content = {}, 
  onChange, 
  errors = {}, 
  touched = {},
  onFieldBlur,
  packType = 'ambassador',
  iosDebugMode = false
}) => {
  const handleChange = (field, value) => {
    onChange({ ...content, [field]: value });
  };

  const requiresBilingual = BILINGUAL_REQUIRED[packType];

  return (
    <div style={{ 
      display: 'flex',
      flexDirection: 'column',
      gap: EMZI_SPACING.md,
    }}>
      {/* PHASE 2 NOTE: Ambassador Name, Role/Title, Voice/Tone now in Basic Info Section (Section 1) */}
      <div style={{
        padding: EMZI_SPACING.sm,
        backgroundColor: '#F0F9FF',
        border: '1px solid #BAE6FD',
        borderRadius: EMZI_RADIUS.md,
        fontSize: '12px',
        color: '#0369A1',
        marginBottom: EMZI_SPACING.md,
      }}>
        ℹ️ <strong>Basic fields moved to Section 1:</strong> Ambassador Name, Role/Title, and Voice/Tone are now part of "Basic Information" for faster access.
      </div>

      {/* Bio/Background (Bilingual) */}
      <div style={{ marginBottom: EMZI_SPACING.md }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '500',
          marginBottom: EMZI_SPACING.xs,
          color: EMZI_COLORS.textPrimary,
        }}>
          Bio/Background
          <span style={{ color: EMZI_COLORS.error, marginLeft: '4px' }}>*</span>
        </label>
        
        <div style={{
          fontSize: '12px',
          color: EMZI_COLORS.textSecondary,
          marginBottom: EMZI_SPACING.xs,
          padding: EMZI_SPACING.xs,
          background: EMZI_COLORS.surface,
          borderRadius: EMZI_RADIUS.sm,
          borderLeft: `3px solid ${EMZI_COLORS.primary}`,
        }}>
          💡 Brief background agents can reference. Example: "An inspirational figure championing balanced living through faith and natural wellness. Trusted voice promoting healthy lifestyle aligned with Islamic values."
        </div>

        <ControlledTextarea
          value={content.bio || ''}
          onChange={(value) => handleChange('bio', value)}
          onBlur={() => onFieldBlur && onFieldBlur('bio')}
          placeholder="e.g., An inspirational figure championing balanced living through faith and natural wellness, promoting moderation and holistic health for Muslim communities..."
          rows={4}
          debugMode={iosDebugMode}
          fieldName="bio"
          style={{
            width: '100%',
            padding: EMZI_SPACING.sm,
            border: `1px solid ${touched.bio && errors.content?.bio ? EMZI_COLORS.error : EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            fontFamily: 'inherit',
            outline: 'none',
            resize: 'vertical',
          }}
        />
        {touched.bio && errors.content?.bio && (
          <div style={{
            marginTop: '4px',
            fontSize: '12px',
            color: EMZI_COLORS.error,
          }}>
            {errors.content.bio}
          </div>
        )}
      </div>

      {/* Key Achievements */}
      <StructuredListInput
        label="Key Achievements"
        items={content.key_achievements || []}
        onChange={(items) => handleChange('key_achievements', items)}
        minItems={1}
        maxItems={10}
        placeholder="Add an achievement..."
        helper="Notable accomplishments. Example: 'Winner MasterChef Malaysia 2023' or 'Featured on Al-Hijrah TV'"
        required={true}
        hasError={touched.key_achievements && errors.content?.key_achievements}
        errorMessage={errors.content?.key_achievements}
      />

      {/* Why This Ambassador */}
      <div style={{ marginBottom: EMZI_SPACING.md }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '500',
          marginBottom: EMZI_SPACING.xs,
          color: EMZI_COLORS.textPrimary,
        }}>
          Why This Ambassador?
          <span style={{
            fontSize: '11px',
            color: EMZI_COLORS.textMuted,
            marginLeft: EMZI_SPACING.xs,
            fontWeight: '400',
          }}>
            (Optional)
          </span>
        </label>
        
        <div style={{
          fontSize: '12px',
          color: EMZI_COLORS.textSecondary,
          marginBottom: EMZI_SPACING.xs,
          padding: EMZI_SPACING.xs,
          background: EMZI_COLORS.surface,
          borderRadius: EMZI_RADIUS.sm,
          borderLeft: `3px solid ${EMZI_COLORS.primary}`,
        }}>
          💡 Why they're a good fit for our brand. Example: "Aligns with our values of health & halal living; trusted by Muslim families"
        </div>

        <textarea
          value={content.why_ambassador || ''}
          onChange={(e) => handleChange('why_ambassador', e.target.value)}
          placeholder="e.g., Aligns with our values of health & halal living; trusted by Muslim families"
          rows={3}
          style={{
            width: '100%',
            padding: EMZI_SPACING.sm,
            border: `1px solid ${EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            fontFamily: 'inherit',
            outline: 'none',
            resize: 'vertical',
          }}
        />
      </div>

      {/* Talking Points */}
      <StructuredListInput
        label="Approved Talking Points"
        items={content.talking_points || []}
        onChange={(items) => handleChange('talking_points', items)}
        minItems={0}
        maxItems={12}
        placeholder="Add a talking point..."
        helper="Key messages agents can mention. Example: 'Chef Amira personally uses our olive oil in her cooking' or 'Recommends for heart health'"
        required={false}
        hasError={false}
      />

      {/* Partnership Details */}
      <div style={{ marginBottom: EMZI_SPACING.md }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '500',
          marginBottom: EMZI_SPACING.xs,
          color: EMZI_COLORS.textPrimary,
        }}>
          Partnership Details
          <span style={{
            fontSize: '11px',
            color: EMZI_COLORS.textMuted,
            marginLeft: EMZI_SPACING.xs,
            fontWeight: '400',
          }}>
            (Optional)
          </span>
        </label>
        
        <div style={{
          fontSize: '12px',
          color: EMZI_COLORS.textSecondary,
          marginBottom: EMZI_SPACING.xs,
          padding: EMZI_SPACING.xs,
          background: EMZI_COLORS.surface,
          borderRadius: EMZI_RADIUS.sm,
          borderLeft: `3px solid ${EMZI_COLORS.primary}`,
        }}>
          💡 Partnership terms agents need to know. Example: "Exclusive partnership Jan-Dec 2025, featured in all campaigns"
        </div>

        <textarea
          value={content.partnership_details || ''}
          onChange={(e) => handleChange('partnership_details', e.target.value)}
          placeholder="e.g., Exclusive partnership Jan-Dec 2025, featured in all campaigns"
          rows={2}
          style={{
            width: '100%',
            padding: EMZI_SPACING.sm,
            border: `1px solid ${EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            fontFamily: 'inherit',
            outline: 'none',
            resize: 'vertical',
          }}
        />
      </div>

      {/* Social Media */}
      <StructuredListInput
        label="Social Media Handles"
        items={content.social_media || []}
        onChange={(items) => handleChange('social_media', items)}
        minItems={0}
        maxItems={8}
        placeholder="Add a social handle..."
        helper="Their social media presence. Example: '@ChefAmira (Instagram, 500K followers)' or 'YouTube: Chef Amira Kitchen'"
        required={false}
        hasError={false}
      />

      {/* Do's and Don'ts */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: window.innerWidth > 768 ? '1fr 1fr' : '1fr',
        gap: EMZI_SPACING.md,
      }}>
        <StructuredListInput
          label="Do's (When Mentioning)"
          items={content.dos || []}
          onChange={(items) => handleChange('dos', items)}
          minItems={0}
          maxItems={6}
          placeholder="Add a do..."
          helper="What agents should say. Example: 'Emphasize faith-based motivation and moderation' or 'Highlight natural wellness approach'"
          required={false}
        />

        <StructuredListInput
          label="Don'ts"
          items={content.donts || []}
          onChange={(items) => handleChange('donts', items)}
          minItems={0}
          maxItems={6}
          placeholder="Add a don't..."
          helper="What to avoid. Example: 'Don't make medical claims or commercialize religious context' or 'Avoid unverified health promises'"
          required={false}
        />
      </div>

      {/* Completion hint */}
      <div style={{
        padding: EMZI_SPACING.sm,
        background: '#e6f7ed',
        borderRadius: EMZI_RADIUS.sm,
        fontSize: '12px',
        color: EMZI_COLORS.success,
        borderLeft: `3px solid ${EMZI_COLORS.success}`,
      }}>
        ✅ <strong>Pro Tip:</strong> Ambassador packs help agents leverage credibility. Provide approved talking points and clear do's/don'ts!
      </div>
    </div>
  );
};

export default EnhancedAmbassadorForm;
